import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '@utils/axiosInstance';
import { useSelector, useDispatch } from 'react-redux';
import { toast, Flip } from 'react-toastify';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import TextInput from './TextInput';
import { setChapterList, addChapter, updateChapter, deleteChapter, setSelectedChapter } from '@modules/chapter';
import { milliSecondsToFormattedTime } from '@utils/milliSecondsToFormattedTime';
import { handleTimeInputChange, saveChaptersToServer as saveChapters } from '@utils/timeUtils';
import { parseFormattedTime } from '@utils/parseFormattedTime';
const ACTIVE_REGION_COLOR = "rgba(0, 123, 255, 0.2)";

function ChapList({ idx, item }) {
    const dispatch = useDispatch();
    const { uuid, lang } = useParams();

    const playTime = useSelector(state => state.media.playTime);
    const videoDurationTime = useSelector(state => state.media.videoDurationTime);
    const userToken = useSelector(state => state.organization.userToken);
    const organizationData = useSelector(state => state.organization.organizationData)
    const chapterList = useSelector(state => state.chapter.chapterList);

    const [isSelected, setIsSelected] = useState(false);
    const [colors, setColors] = useState('black');
    const [changingChapter, setChangingChapter] = useState({ ...item });
    const [file, setFile] = useState(null);
    const [isChanged, setIsChanged] = useState(false);

    const labelRef = useRef(null);
    const descRef = useRef(null);
    const startTimeRef = useRef(null);

    // 현재 재생 시간이 자막 시간 범위 내에 있는지 확인
    useEffect(() => {
        if (playTime && item) {
            if (chapterList[idx + 1]) {
                setIsSelected(item.start <= playTime && chapterList[idx + 1].start > playTime);
                console.log('chapterList', chapterList, item.start, playTime, chapterList[idx + 1].start);
            }
            else {
                setIsSelected(item.start <= playTime && videoDurationTime > playTime);
            }
        }
    }, [playTime, item, chapterList]);

    // 초기 값 설정
    useEffect(() => {
        if (item) {
            updateTimeValues();
            setChangingChapter({ ...item });
            setColors('black');
        }
    }, [item]);

    // 시간 값 업데이트 함수
    const updateTimeValues = useCallback(() => {
        if (startTimeRef.current) {
            startTimeRef.current.textContent = milliSecondsToFormattedTime(item.start);
        }
    }, [item]);

    // 변경사항 저장
    const saveChanges = useCallback((type) => {
        setColors('black');
        setIsChanged(false);

        console.log('saveChanges', changingChapter.start, chapterList[idx - 1].start);
        // 이전 자막과 겹치는지 확인
        if (idx > 0 && changingChapter.start < chapterList[idx - 1].start) {
            toast.error("⚠️ 시작 시간이 이전 챕터보다 빠를 수 없습니다. 이전 챕터의 시작 시간 이후로 조정해주세요.", {
                width: 300,
                position: "top-center",
                transition: Flip
            });
            updateTimeValues();
            return;
        }

        // 다음 자막과 겹치는지 확인
        if (idx < chapterList.length - 1 && changingChapter.start > chapterList[idx + 1].start) {
            toast.error("⚠️ 시작 시간이 다음 챕터보다 늦을 수 없습니다. 다음 챕터의 시작 시간 이전으로 조정해주세요.", {
                position: "top-center",
                transition: Flip
            });
            updateTimeValues();
            return;
        }

        let newStart = type === 'start' ? changingChapter.start : chapterList[idx].start;

        const newChapter = { ...changingChapter, start: newStart };
        const updatedList = [...chapterList];
        updatedList[idx] = newChapter;

        saveChaptersToServer(updatedList);
        dispatch(updateChapter(idx, { start: newStart }));
    }, [changingChapter, idx, chapterList, updateTimeValues]);

    // 챕터 추가
    const addChapterBlock = useCallback(() => {
        const arr = chapterList.map(el => Number(el.index));
        const maxId = Math.max(...arr, 0); // 빈 배열 대비
        const MIN_DURATION = 0.1;
        const SAFETY_MARGIN = 0.05;

        let newChapter;

        if (idx === chapterList.length - 1) {
            // 마지막 챕터 뒤에 추가
            const newStart = Math.min(item.start + 1, videoDurationTime - MIN_DURATION);

            newChapter = {
                index: maxId + 1,
                type: "seeker",
                start: newStart,
                label: "",
                thumbnail: "",
                desc: ""
            };
        } else {
            // 중간에 삽입
            const currentStart = item.start;
            const nextStart = chapterList[idx + 1].start;
            console.log('idx', idx, 'currentStart', currentStart, 'nextStart', nextStart);
            const availableStart = currentStart + SAFETY_MARGIN;
            const availableEnd = nextStart - SAFETY_MARGIN;

            const availableDuration = availableEnd - availableStart;
            console.log('availableDuration', availableDuration, 'availableStart', availableStart, 'availableEnd', availableEnd);
            if (availableDuration >= MIN_DURATION) {
                newChapter = {
                    index: maxId + 1,
                    type: "seeker",
                    start: availableStart,
                    label: "",
                    thumbnail: "",
                    desc: ""
                };
            } else {
                toast.warn("⚠️ 챕터 사이 여유가 너무 좁아 삽입할 수 없습니다.", {
                    position: "top-center",
                    transition: Flip,
                    toastClassName: "custom-toast-width"
                });
                return;
            }
        }

        dispatch(addChapter(idx, newChapter));

        const newChapters = [
            ...chapterList.slice(0, idx + 1),
            newChapter,
            ...chapterList.slice(idx + 1)
        ];

        saveChaptersToServer(newChapters);
    }, [idx, item, chapterList, videoDurationTime, dispatch]);

    // 챕터 삭제
    const deleteChapterBlock = useCallback(() => {
        dispatch(deleteChapter(idx));

        let newChapterList = [...chapterList];

        if (newChapterList.length <= 1) {
            const arr = newChapterList.map(el => Number(el.index));
            const maxId = Math.max(...arr);
            newChapterList = [{
                "index": maxId + 1,
                "type": "seeker",
                "start": 0,
                "label": "Chapter 1",
                "thumbnail": "",
                "desc": ""
            }]

        } else {
            newChapterList.splice(idx, 1);
        }

        saveChaptersToServer(newChapterList);
    }, [idx, chapterList, dispatch]);

    // 서버에 자막 저장
    const saveChaptersToServer = useCallback((chapters) => {
        return saveChapters(chapters, {
            api,
            userToken,
            uuid,
            orgCode: organizationData.organization_code,
            lang
        });
    }, [userToken, uuid, organizationData, lang]);

    // 자막 선택
    const handleChapListClick = useCallback(() => {
        dispatch(setSelectedChapter(item));
    }, [dispatch, item]);

    // 시간 변경 처리
    const handleStartTimeChange = useCallback((e) => {
        setIsChanged(true);

        handleTimeInputChange(e, {
            setColors,
            setChangingItem: setChangingChapter,
            item,
            timeField: 'start'
        });
    }, [item]);

    const changeTime = (e) => {
        const formattedTime = e.target.textContent;
        try {
            const timeInMilliseconds = parseFormattedTime(formattedTime);
            console.log('timeInMilliseconds', timeInMilliseconds);
            setChangingChapter(prev => ({
                ...prev,
                start: timeInMilliseconds
            }));
            updateTimeValues();
            setColors('green');
        } catch (error) {
            console.error('Invalid time format:', error);
            toast.error("유효하지 않은 시간 형식입니다", {
                position: "top-center",
                transition: Flip
            });
            setColors('red');
        }
    };

    function getUploadUrl(e) {
        const file = e.target.files[0];
        setFile(file);
        console.log('file', file);
        const tmpList = [...chapterList];
        tmpList[idx] = { ...tmpList[idx], thumbnail: URL.createObjectURL(file) };
        dispatch(setChapterList(tmpList));

        api({
            method: 'post',
            url: '/chapter/getThumbUploadUrl',
            data: {
                tk: userToken,
                uuid: uuid,
                orc: organizationData.organization_code,
                index: idx + 1,
                lang: lang
            }
        })
            .then((response) => {
                if (response.data.code === 100) {
                    uploadFile(response.data.upload_url, file, idx);
                }
            })
            .catch((error) => {
                console.error('Error getting upload URL:', error);
            });
    }

    function uploadFile(uploadUrl, file, idx) {
        api({
            method: 'put',
            url: uploadUrl,
            headers: {
                'Content-Type': 'image/png'
            },
            data: file
        })
            .then((response) => {
                console.log('response', response);
                // return api({
                //     method: 'post',
                //     url: '/common/setThumb',
                //     data: {
                //         tk: userToken,
                //         uuid: uuid,
                //         ogc: organizationData.organization_code,
                //         idx: idx + 1
                //     }
                // });
            })
            .then((response) => {
                if (response.data.code === 100) {
                    console.log('Thumbnail set successfully');
                }
            })
            .catch((error) => {
                console.error('Error uploading file or setting thumbnail:', error);
            });
    }

    return (
        <div
            className={`chapList ${isSelected ? 'active' : 'inactive'} ${isChanged ? 'changed' : ''}`}
            onClick={handleChapListClick}
        >
            <Typography variant="h6" className="chapter-number">
                {idx.toString().padStart(2, '0')}
            </Typography>
            <div className="chapter-time">
                <Typography variant="caption">seeker</Typography>
                <div
                    ref={startTimeRef}
                    className='editable-time'
                    contentEditable="true"
                    onInput={handleStartTimeChange}
                    onBlur={() => saveChanges('start')}
                    suppressContentEditableWarning={true}
                    style={{ marginBottom: 7, color: colors }} />
            </div>
            <div className="chapter-thumbnail">
                {item.thumbnail ? (
                    <img src={item.thumbnail} alt="Thumbnail" className="thumbnail-image" />
                ) : (
                    <label htmlFor={`chapterFile_${idx}`}>
                        <IconButton component="span" color="primary">
                            <AddPhotoAlternateIcon />
                        </IconButton>
                    </label>
                )}
                <input
                    id={`chapterFile_${idx}`}
                    type="file"
                    style={{ display: "none" }}
                    accept="image/png"
                    onChange={getUploadUrl} />
            </div>
            <div className="chapter-content">
                <TextInput
                    idx={idx}
                    text={item.label}
                    el={item}
                    type="label"
                    setIsChanged={setIsChanged}
                />
                <TextInput
                    idx={idx}
                    text={item.desc}
                    el={item}
                    type="desc"
                    setIsChanged={setIsChanged}
                />
            </div>

            <div className="chapter-actions">
                {chapterList.length > 1 && (
                    <IconButton onClick={deleteChapterBlock} size="small">
                        <DeleteIcon />
                    </IconButton>
                )}
            </div>
            <div className="btn-add-chapter" onClick={addChapterBlock}>
                <AddCircleOutlineIcon />
            </div>
        </div>
    );
}

export default ChapList;
